import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { hasPermission } from '@/composables/usePermission'

export default function useCrimeList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications();
  const { handleError } = useCommon()
  const { updateItem } = useLambda()
  const { crimesQueryWithOffset } = useMongoDBFunctions()
  const refCrimeListTable = ref(null)
  const crimes = ref([])
  const userData = store.state?.userStore?.userData

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      {
        key: 'Actions',
        label: i18n.t('message.tableHeader.actions'),
      },
      {
        key: 'name',
        label: i18n.t('message.tableHeader.name'),
        sortable: true,
      },
      {
        key: 'lawNumber',
        label: i18n.t('label.lawNumber'),
        sortable: true,
      },
      {
        key: 'law',
        label: i18n.t('label.lawName'),
        sortable: true,
      },
      {
        key: 'article',
        label: i18n.t('label.article'),
        sortable: true,
      },
      {
        key: 'number',
        label: i18n.t('label.number'),
        sortable: true,
      },
      {
        key: 'description',
        label: i18n.t('message.tableHeader.description'),
        sortable: true,
      },
    ]
  })

  const perPage = ref(10)
  const totalCrimes = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const statusFilter = ref(null)
  const isLoading = ref(true)

  const fetchCrimes = async () => {
    isLoading.value = true

    try {
      const input = {
        limit: perPage.value,
        total: currentPage.value === 1,
        offset: (currentPage.value * perPage.value) - perPage.value,
        query: {},
        search: searchQuery.value,
        sortBy: sortBy.value,
        sortOrder: isSortDirDesc.value ? -1 : 1,
        client_id: ( userData.role != "admin" ) ? userData.client.$oid : null
      }

      const { crimes: c, totalRecords } = await crimesQueryWithOffset(input)
    
      crimes.value = c
      if (currentPage.value === 1) totalCrimes.value = totalRecords
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.crime_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const deleteCrime = async (id) => {
    if (!hasPermission('crimes', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    try {
      const query = { _id: oid(id), client_id: oid(userData.client.$oid), deleted: { $ne: true } }
      const payload = { deleted: true }
      const action = { $set: payload }

      await updateItem({ collection: 'crime', query, action })

      showSuccessMessage(i18n.t('message.crime_remove'))
      fetchCrimes()
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }


  watch([currentPage, perPage, statusFilter], () => {
    fetchCrimes()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchCrimes()
  })

  return {
    fetchCrimes,
    tableColumns,
    perPage,
    currentPage,
    totalCrimes,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refCrimeListTable,
    crimes,
    statusFilter,
    deleteCrime,
    isLoading,
  }
}